<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-surface-50">
    <Loader v-if="isLoading" class="self-center my-auto" />
    <div v-else class="flex flex-col w-full md:max-w-[560px] px-3.5 pb-6">
      <settings-group-label>חלון ההזמנה</settings-group-label>
      <settings-card>
        <!-- Slot interval -->
        <div class="py-3 border-b border-surface-100">
          <div class="text-sm font-medium mb-0.5">שעת התחלה של תור</div>
          <div class="text-xs text-surface-500 mb-2.5">כל כמה דקות ייפתח חלון זמן חדש להזמנה</div>
          <settings-chips
            :modelValue="schedulingSettings.appointmentsStepGap"
            :options="stepGapOptions"
            @update:modelValue="(v) => schedulingSettings.appointmentsStepGap = v"
          />
          <div class="flex items-center gap-1.5 mt-2.5 overflow-hidden">
            <span
              v-for="(slot, i) in previewSlots"
              :key="slot"
              class="flex-none text-[10.5px] px-2.5 py-1 rounded-lg border"
              :class="i === 0
                ? 'font-semibold bg-primary-100 text-primary-700 border-primary-200'
                : 'bg-surface-50 text-surface-500 border-surface-100'"
            >{{ slot }}</span>
            <span class="text-xs text-surface-400">…</span>
          </div>
          <div class="text-[10px] text-surface-400 mt-1.5">כך ייפתחו חלונות ההזמנה ללקוח</div>
        </div>

        <!-- Gap between appointments -->
        <div class="py-3 border-b border-surface-100">
          <div class="text-sm font-medium mb-0.5">רווח בין תורים</div>
          <div class="text-xs text-surface-500 mb-2.5">זמן פנוי שיישמר לפני ואחרי כל תור — לניקיון, סידור והתארגנות</div>
          <settings-chips
            :modelValue="schedulingSettings.appointmentsBufferMinutes ?? 0"
            :options="bufferOptions"
            @update:modelValue="(v) => schedulingSettings.appointmentsBufferMinutes = v"
          />
          <div class="text-[10px] text-surface-400 mt-2">{{ bufferHint }}</div>
        </div>

        <!-- Max days ahead -->
        <div class="flex items-center gap-3 py-3 border-b border-surface-100">
          <div class="flex-1 min-w-0 me-4">
            <div class="text-sm font-medium">כמה ימים קדימה אפשר להזמין</div>
            <div class="text-xs text-surface-500 mt-1">לקוחות יראו את היומן עד {{ schedulingSettings.allowToSchedule }} ימים מהיום</div>
          </div>
          <settings-stepper
            :modelValue="schedulingSettings.allowToSchedule ?? 14"
            :min="1"
            :max="365"
            suffix="ימים"
            @update:modelValue="(v) => schedulingSettings.allowToSchedule = v"
          />
        </div>

        <!-- Start date — mobile opens a bottom-sheet calendar; desktop keeps the inline picker -->
        <div class="flex items-center gap-3 py-3">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium">תאריך התחלה להזמנת תור</div>
            <div class="text-xs text-surface-500 mt-1">לפני תאריך זה לא ניתן להזמין</div>
          </div>
          <button
            v-if="isMobile"
            type="button"
            class="flex items-center gap-1.5 px-3 py-[7px] rounded-[10px] border border-surface-200 text-xs font-medium flex-none"
            @click="openStartDateSheet"
          >
            <i class="pi pi-calendar text-[13px] text-surface-500" /> {{ startDateLabel }}
          </button>
          <DatePicker
            v-else
            id="startSchedulingDate"
            v-model="schedulingSettings.startSchedulingDate as Date"
            inputId="dateformat"
            dateFormat="dd-mm-yy"
            class="calendar w-32 flex-none"
            :inputClass="'!text-sm'"
            touchUI />
        </div>
      </settings-card>

      <settings-group-label>אישורים וביטולים</settings-group-label>
      <settings-card>
        <settings-toggle-row
          title="אישור תורים ידני"
          description="כל תור ימתין לאישור שלכם לפני שייכנס ליומן"
          :status="schedulingSettings.appointmentManualApprove ?? false"
          @value-changed="(val) => schedulingSettings.appointmentManualApprove = val"
        />
        <settings-toggle-row
          title="חסימת הזמנת תור לאותו היום"
          description="לקוחות לא יוכלו להזמין תור ליום הנוכחי"
          :status="schedulingSettings.isDisableScheduleSameDay ?? false"
          @value-changed="(val) => schedulingSettings.isDisableScheduleSameDay = val"
        />
        <settings-toggle-row
          title="מניעת ביטול תור"
          description="לקוחות לא יוכלו לבטל תור ברגע האחרון"
          :status="preventCancellationBefore"
          last
          @value-changed="(val) => { preventCancellationBefore = val; schedulingSettings.allowToCancel = !val; }"
        >
          <div class="flex items-center gap-3 bg-surface-50 rounded-xl px-3 py-2.5">
            <span class="flex-1 text-xs text-surface-600">חסימת ביטול החל מ־</span>
            <settings-stepper
              :modelValue="schedulingSettings.cancelHoursBeforeLimit ?? 24"
              :min="2"
              :max="48"
              suffix="שעות לפני"
              @update:modelValue="(v) => schedulingSettings.cancelHoursBeforeLimit = v"
            />
          </div>
        </settings-toggle-row>
        <div class="pt-3 border-t border-surface-100">
          <label class="block text-[11px] font-semibold text-surface-500 mb-1.5">מדיניות ביטולים</label>
          <Textarea
            v-model="schedulingSettings.scheduleCancellationPolicyText"
            :autoResize="false"
            rows="4"
            maxlength="1000"
            class="w-full"
            placeholder="המדיניות תוצג ללקוח בעת קביעת התור"
            :disabled="loadingCta"
          />
        </div>
      </settings-card>

      <settings-group-label>עדכונים לצוות</settings-group-label>
      <settings-card>
        <settings-toggle-row
          title="עדכוני תורים במייל"
          description="אנשי הצוות יקבלו מייל עם זימון ליומן בכל קביעה, שינוי או ביטול של תור שלהם"
          :status="schedulingSettings.sendAppointmentEmailUpdates ?? true"
          last
          @value-changed="(val) => schedulingSettings.sendAppointmentEmailUpdates = val"
        />
      </settings-card>

      <settings-group-label>חוויית הזמנה חכמה</settings-group-label>
      <settings-card>
        <settings-toggle-row
          title="בחירת מספר שירותים"
          description="לקוחות יוכלו לבחור יותר משירות אחד בתור"
          :status="schedulingSettings.allowMultipleServices ?? true"
          @value-changed="(val) => schedulingSettings.allowMultipleServices = val"
        />
        <settings-toggle-row
          title="הצעת שעות בהמשך לתורים קיימים"
          badge="חדש"
          :description="offGridDescription"
          :status="schedulingSettings.allowOffGridSlotsOnWebsite ?? false"
          @value-changed="(val) => schedulingSettings.allowOffGridSlotsOnWebsite = val"
        />
        <settings-toggle-row
          title="התראה ללקוחות על איחור"
          badge="חדש"
          description="המערכת מזהה תור שמתארך ומעדכנת אוטומטית את הלקוחות הבאים"
          :status="schedulingSettings.sendDelayNotifications ?? false"
          @value-changed="(val) => schedulingSettings.sendDelayNotifications = val"
        >
          <div class="bg-surface-50 rounded-xl px-3 py-1">
            <settings-radio-group
              :modelValue="schedulingSettings.delayNotificationsChannel ?? 'sms'"
              last
              :options="[
                { value: 'sms', label: 'SMS', desc: 'העדכון נשלח ללקוח/ה בהודעת SMS' },
                { value: 'whatsapp', label: 'WhatsApp', desc: 'הודעת WhatsApp · אם ההודעה לא נמסרת, נשלח SMS במקומה' },
              ]"
              @update:modelValue="(v) => schedulingSettings.delayNotificationsChannel = v"
            />
          </div>
        </settings-toggle-row>
        <settings-toggle-row
          title="סנכרון ללוח חגים"
          badge="חדש"
          description="ימי חג ייחסמו אוטומטית ביומן; תבחרו אם אתם עובדים בערבי חג"
          :status="schedulingSettings.holidaySettings?.syncWithHolidays ?? false"
          last
          @value-changed="(val) => schedulingSettings.holidaySettings.syncWithHolidays = val"
        >
          <div class="bg-surface-50 rounded-xl px-3 py-1">
            <settings-toggle-row
              title="עבודה בערב חג"
              description="האם אתם עובדים בערב חג?"
              :status="schedulingSettings.holidaySettings?.workOnHolidaysEves ?? false"
              :last="!schedulingSettings.holidaySettings?.workOnHolidaysEves"
              @value-changed="(val) => schedulingSettings.holidaySettings.workOnHolidaysEves = val"
            />
            <div
              v-if="schedulingSettings.holidaySettings?.workOnHolidaysEves"
              class="flex items-center gap-3 py-3"
            >
              <span class="flex-1 text-xs text-surface-600">שעת סיום בערב חג</span>
              <DatePicker
                id="datepicker-timeonly"
                v-model="holidayEveWorkingHours"
                placeholder="בחרו שעה"
                :pt="{
                  panel: { style: 'direction:ltr' }
                }"
                :stepMinute="15"
                timeOnly
                class="w-28 flex-none" />
            </div>
          </div>
        </settings-toggle-row>
      </settings-card>

      <settings-group-label>תצוגת היומן</settings-group-label>
      <settings-card>
        <div class="py-3">
          <div class="text-sm font-medium mb-0.5">גובה השעה ביומן</div>
          <div class="text-xs text-surface-500 mb-2.5">שורות גבוהות יותר מציגות יותר פרטים בכל תור</div>
          <settings-chips
            :modelValue="schedulingSettings.calendarRowHeight ?? DEFAULT_CALENDAR_ROW_HEIGHT"
            :options="rowHeightOptions"
            @update:modelValue="(v) => schedulingSettings.calendarRowHeight = v"
          />
          <!-- Live preview: one hour of the calendar at the chosen height. Uses the very
               same layout rules as the calendar so what is shown here is what is rendered. -->
          <div class="mt-3 rounded-xl border border-surface-200 overflow-hidden bg-white">
            <div class="flex" :style="{ height: `${previewRowHeight}px` }">
              <div class="w-11 flex-none border-e border-surface-100 text-[10px] text-surface-400 text-center pt-1">09:00</div>
              <div class="flex-1 p-1.5">
                <div class="h-full rounded-lg bg-primary-50 border-s-[3px] border-primary px-2 py-1 overflow-hidden leading-tight">
                  <template v-if="previewLayout.showText">
                    <!-- Tight blocks: name - service on one line -->
                    <div v-if="!previewLayout.roomy" class="font-medium text-surface-800 truncate" :class="previewLayout.primaryTextClass">
                      דנה לוי - תספורת + פן
                    </div>
                    <!-- Roomy blocks: name and service on separate lines -->
                    <template v-else>
                      <div class="font-medium text-surface-800 truncate" :class="previewLayout.primaryTextClass">דנה לוי</div>
                      <div class="text-surface-500 truncate" :class="previewLayout.secondaryTextClass">תספורת + פן</div>
                    </template>
                    <div
                      v-if="previewLayout.showTimeRange"
                      class="text-[11px] text-surface-400 mt-0.5"
                      style="direction: ltr;">
                      09:00 - 10:00
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </settings-card>

      <!-- Messages settings moved to automations -->
      <div
        class="mt-3.5 bg-primary-50 border border-primary-100 rounded-2xl px-3.5 py-3 flex items-center gap-3 cursor-pointer"
        @click="goToAutomations"
      >
        <div class="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-none">
          <i class="pi pi-bell text-base text-primary-700" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-xs font-semibold text-primary-800">הגדרות הודעות עברו לאוטומציות</div>
          <div class="text-[11px] text-surface-500 mt-0.5">תזכורות, רשימת המתנה, אישורי תור ובקשות משוב</div>
        </div>
        <i class="pi pi-chevron-left text-sm text-primary-400 flex-none" />
      </div>

      <settings-save-bar v-if="canEdit" :dirty="dirty" :saved="saved" :loading="loadingCta" @save="saveChanges" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAdminStore } from '@app/stores';
import { computed, reactive, ref, watch } from 'vue';
import {
  ALLOW_TO_SCHEDULE_DEFAULT,
  APPOINTMENT_BUFFER_OPTIONS,
  CALENDAR_ROW_HEIGHT_OPTIONS,
  DEFAULT_CALENDAR_ROW_HEIGHT,
  TSettingsScheduling
} from '@torim-app/types';
import { calendarEventLayout } from '@torim-app/utils';
import { SettingsApi } from '@app/api/admin';
import { useToast } from 'vue-toastification';
import { DateTime } from 'luxon';
import { useRouter } from 'vue-router';
import DatePicker from 'primevue/datepicker';
import { trackEvent } from '@app/composables/posthog';
import { AdminRouteNames } from '@app/router/route-names';
import { useResponsive } from '@app/composables/responsive';
import { useDrawer } from '@torim-app/shared-components';
import SettingsDateSheet from './ui/SettingsDateSheet.vue';
import SettingsCard from './ui/SettingsCard.vue';
import SettingsGroupLabel from './ui/SettingsGroupLabel.vue';
import SettingsToggleRow from './ui/SettingsToggleRow.vue';
import SettingsRadioGroup from './ui/SettingsRadioGroup.vue';
import SettingsStepper from './ui/SettingsStepper.vue';
import SettingsChips from './ui/SettingsChips.vue';
import SettingsSaveBar from './ui/SettingsSaveBar.vue';
import { useSettingsForm } from './ui/settings-form';

const adminStore = useAdminStore();
const toast = useToast();
const router = useRouter();

const goToAutomations = () => {
  router.push({ name: AdminRouteNames.Communication, query: { tab: '0' } });
};

const stepGapOptions = ([15, 30, 45, 60, 90, 120] as const).map(n => ({ value: n, label: `${n} דק׳` }));
const bufferOptions = APPOINTMENT_BUFFER_OPTIONS.map(n => ({ value: n as number, label: n === 0 ? 'ללא' : `${n} דק׳` }));
const rowHeightOptions = CALENDAR_ROW_HEIGHT_OPTIONS;

const schedulingSettings: TSettingsScheduling = reactive<TSettingsScheduling>(<TSettingsScheduling>{});
const loadingCta = ref(false);
const holidayEveWorkingHours = ref<Date>();
const preventCancellationBefore = ref(false);

const { dirty, saved, takeSnapshot, flashSaved } = useSettingsForm(() => schedulingSettings);

const isLoading = computed(() => adminStore.getFetchingInProgress);
const siteData = computed(() => adminStore.getBusinessSiteData);
const canEdit = computed(() => adminStore.getCanEdit);

const { isMobile } = useResponsive();
const { openDrawer } = useDrawer();

const startDateLabel = computed(() => {
  const date = schedulingSettings.startSchedulingDate;
  const dt = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(String(date ?? ''));
  return dt.isValid ? dt.toFormat('dd-MM-yyyy') : 'בחרו תאריך';
});

const openStartDateSheet = () => {
  const current = schedulingSettings.startSchedulingDate;
  openDrawer(SettingsDateSheet, {
    date: current instanceof Date && !isNaN(current.getTime()) ? current : undefined,
    onPick: (picked: Date) => { schedulingSettings.startSchedulingDate = picked; },
  }, { title: 'בחירת תאריך' });
};

// The preview box mirrors the chosen height so the choice is legible before saving.
const previewRowHeight = computed(() => schedulingSettings.calendarRowHeight ?? DEFAULT_CALENDAR_ROW_HEIGHT);
// A one-hour visit is exactly one row tall, so the row height is the block height.
const previewLayout = computed(() => calendarEventLayout(previewRowHeight.value, 'day'));

// The first booking slots a customer would see for the chosen interval
const previewSlots = computed(() => {
  const interval = schedulingSettings.appointmentsStepGap || 15;
  return Array.from({ length: 5 }, (_, i) => {
    const minutes = 9 * 60 + i * interval;
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
  });
});

// The off-grid suggestion starts right when the provider is free again — which,
// once a gap is configured, means the end of the previous appointment plus the gap.
const offGridDescription = computed(() => {
  const buffer = schedulingSettings.appointmentsBufferMinutes ?? 0;
  return buffer
    ? `שעות שמתחילות ${buffer} דק׳ אחרי סיום תור קיים יוצעו ללקוחות באתר — יומן צפוף ויעיל יותר`
    : 'שעות שמתחילות מיד בסיום תור קיים יוצעו ללקוחות באתר — יומן צפוף ויעיל יותר';
});

// Explains the effect of the chosen gap with a concrete example
const bufferHint = computed(() => {
  const buffer = schedulingSettings.appointmentsBufferMinutes ?? 0;
  if (!buffer) return 'תורים יוכלו להיקבע אחד מיד אחרי השני';
  const minutes = 10 * 60 + buffer;
  const nextTime = `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
  return `תור שמסתיים ב־10:00 יאפשר את התור הבא רק מ־${nextTime}`;
});

const initData = () => {
  const allowToCancel = siteData.value?.configurations?.allowToCancel ?? true;
  preventCancellationBefore.value = !allowToCancel;
  const dataToAssign: TSettingsScheduling = {
    allowToSchedule: +(siteData.value?.configurations?.allowToSchedule || ALLOW_TO_SCHEDULE_DEFAULT),
    appointmentsStepGap: siteData.value?.configurations?.appointmentsStepGap || 15,
    appointmentsBufferMinutes: siteData.value?.configurations?.appointmentsBufferMinutes ?? 0,
    allowToCancel: allowToCancel,
    cancelHoursBeforeLimit: siteData.value?.configurations?.cancelHoursBeforeLimit ?? 24,
    scheduleCancellationPolicyText: siteData.value?.configurations?.scheduleCancellationPolicyText ?? '',
    startSchedulingDate: DateTime.fromISO(<string>siteData.value?.configurations?.startSchedulingDate).toJSDate() || new Date(),
    appointmentManualApprove: siteData.value?.configurations?.appointmentManualApprove ?? false,
    isDisableScheduleSameDay: siteData.value?.configurations?.isDisableScheduleSameDay ?? false,
    allowOffGridSlotsOnWebsite: siteData.value?.configurations?.allowOffGridSlotsOnWebsite ?? false,
    holidaySettings: siteData.value?.configurations?.holidaySettings ?? {
      holidayEveWorkingHoursEnd: 14.5,
      workOnHolidaysEves: true,
      syncWithHolidays: false
    },
    allowMultipleServices: siteData.value?.configurations?.allowMultipleServices ?? true,
    sendDelayNotifications: siteData.value?.configurations?.sendDelayNotifications ?? false,
    delayNotificationsChannel: siteData.value?.configurations?.delayNotificationsChannel ?? 'sms',
    calendarRowHeight: siteData.value?.configurations?.calendarRowHeight ?? DEFAULT_CALENDAR_ROW_HEIGHT,
    sendAppointmentEmailUpdates: siteData.value?.configurations?.sendAppointmentEmailUpdates ?? true
  };
  holidayEveWorkingHours.value = DateTime.local().startOf('day').plus({ hour: dataToAssign.holidaySettings?.holidayEveWorkingHoursEnd ?? 0 }).toJSDate();
  Object.assign(schedulingSettings, dataToAssign);
  takeSnapshot();
};

const saveChanges = async () => {
  trackEvent('settings_scheduling_saved');
  loadingCta.value = true;
  try {
    await SettingsApi.updateSettingsScheduling(schedulingSettings);
    flashSaved();
    await adminStore.fetchBusinessData();
    initData();
  } catch (e) {
    toast.error('לא הצלחנו לעדכן את האתר שלך');
  }
  loadingCta.value = false;
};

watch(holidayEveWorkingHours, () => {
  if (holidayEveWorkingHours.value && schedulingSettings.holidaySettings) {
    schedulingSettings.holidaySettings.holidayEveWorkingHoursEnd = holidayEveWorkingHours.value.getHours() + (holidayEveWorkingHours.value.getMinutes() / 60);
  }
});

initData();

defineExpose({ saveChanges, loadingCta, dirty });
</script>
